import React, { useCallback, useEffect, useState } from 'react';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { translate } from 'components/Translation';
import Button from 'components/CustomButtons/Button';
import Table from 'components/CustomTable/CustomTable';
import TableHeader from 'components/TableHeader/TableHeader';
import { LinkCell, TextCell } from 'components/Cells/Cells';
import AddNumberDialog from 'views/Users/Details/Numbers/subcomponents/AddNumberDialog';
import { capitalize } from 'utils/converters/capitalize';
import CanPerform from 'components/CanPerform/CanPerform';
import Add from '@material-ui/icons/Add';
import Phone from '@material-ui/icons/PhoneOutlined';
import { AccountsService } from 'services/accounts/accounts';
import { NumbersService } from 'services/numbers/numbers';
import { DepartmentsService } from 'services/departments/departments';
import { useDispatch, useSelector } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { finalize } from 'rxjs/operators';
import styles from 'assets/jss/material-dashboard-pro-react/views/users/numbersStyle';
import { makeStyles } from '@material-ui/core';
import { userTypeSelector } from 'redux/modules/session/selectors';
import { BodyM } from 'components/Typography/Typography';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { SingleNumber } from 'utils/api/models/numbers/singleNumber';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { exportToXLS } from 'utils/export/export';
import moment from 'moment';
import { setToast } from 'redux/modules/toast/actions';
import invalidateCache from 'utils/api/invalidateCache';
import { numberDetailsPath } from 'utils/paths/numberDetailsPath';

// @ts-ignore
const useStyles = makeStyles(styles);

interface INumbersProps {
	match: {
		params: {
			userId: string;
		};
	};
	user: types.users.IExtendedCorpUserDetails;
}

const Numbers = ({ match, user }: INumbersProps) => {
	const [departments, setDepartments] = useState<SingleDepartment[]>([]);
	const [loadingDepartments, setLoadingDepartments] = useState<boolean>(true);
	const [loadingNumbers, setLoadingNumbers] = useState<boolean>(true);
	const [loadingAvailableNumbers, setLoadingAvailableNumbers] = useState<boolean>(true);
	const [numbers, setNumbers] = useState<SingleNumber[]>([]);
	const [availableNumbers, setAvailableNumbers] = useState<SingleNumber[]>([]);
	const [searchValue, setSearchValue] = useState<string>('');
	const [filteredNumbers, setFilteredNumbers] = useState<SingleNumber[]>([]);
	const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
	const [isAddNumberDialogOpen, setIsAddNumberDialogOpen] = useState<boolean>(false);
	const { userId } = match.params;
	const dispatch = useDispatch();
	const classes = useStyles();
	const userType = useSelector(userTypeSelector);
	const loading: boolean = loadingNumbers || loadingDepartments || loadingAvailableNumbers;

	const fetchNumbers = useCallback(() => {
		setLoadingNumbers(true);
		NumbersService.getNumbersForUser(userId)
			.pipe(finalize(() => setLoadingNumbers(false)))
			.subscribe(({ numbers }) => setNumbers(numbers));
	}, [userId]);

	useEffect(() => {
		fetchNumbers();
	}, [fetchNumbers]);

	useEffect(() => {
		if (user) {
			if (['ADMIN', 'SUPERADMIN'].includes(userType)) {
				AccountsService.getNumbersForAccount(user.userData.accountId, true)
					.pipe(finalize(() => setLoadingAvailableNumbers(false)))
					.subscribe(({ numbers }) => setAvailableNumbers(numbers));
			} else {
				setLoadingAvailableNumbers(false);
			}
		}
	}, [user, userType]);

	useEffect(() => {
		DepartmentsService.getDepartmentsForCurrentUser()
			.pipe(finalize(() => setLoadingDepartments(false)))
			.subscribe(({ departments }) => {
				setDepartments(departments);
			});
	}, []);

	useEffect(() => {
		if (!loadingNumbers && !loadingAvailableNumbers && !loadingDepartments) {
			dispatch(hideLoadingIndicator());
		} else {
			dispatch(showLoadingIndicator());
		}
	}, [loadingNumbers, loadingAvailableNumbers, loadingDepartments, dispatch]);

	useEffect(() => {
		const lowerCaseSearchValue = searchValue.toLowerCase();
		setFilteredNumbers(
			numbers.filter((row) =>
				['accountNum', 'billingType', 'accountType'].some((key) =>
					String(row[key])
						.toLowerCase()
						.includes(lowerCaseSearchValue)
				)
			)
		);
	}, [numbers, searchValue]);

	const numbersSuccessfullyAdded = (addedNumbers: string[]): void => {
		const newNumbers = availableNumbers
			.filter((number) => addedNumbers.indexOf(number.accountNum) >= 0)
			.map((number) => ({
				...number,
				userId: user.userData.userId,
			}));
		setNumbers((numbers) => [...numbers, ...newNumbers]);
		setAvailableNumbers((numbers) => {
			return numbers.filter((number) => addedNumbers.indexOf(number.accountNum) < 0);
		});
	};

	const numberDetails = (number: SingleNumber): string => {
		return numberDetailsPath(
			number.accountNum,
			user.userData.accountId,
			number.accountType || '',
			number.billingType || '',
			number.upAccountId || ''
		);
	};

	const exportToXls = useCallback(() => {
		const preparedData = (selectedIndexes.length === 0
			? filteredNumbers
			: selectedIndexes.map((index) => filteredNumbers[index])
		).map((number) => ({
			'Phone number': number.accountNum,
			'Service Type': `${capitalize(number.accountType)} - ${capitalize(number.billingType)}`,
		}));

		exportToXLS(preparedData, `numbers ${moment().format('LLL')}`);
	}, [selectedIndexes, filteredNumbers]);

	const deleteNumbers = () => {
		const selectedNumbers = selectedIndexes
			.map((index) => filteredNumbers[index])
			.map((number) => number.accountNum);
		if (selectedNumbers.length > 0) {
			dispatch(showLoadingIndicator());
			NumbersService.detachNumbersFromUser(userId, selectedNumbers)
				.pipe(finalize(() => dispatch(hideLoadingIndicator())))
				.subscribe(({ response }) => {
					if (response?.detachNumbersFromUser?.result) {
						dispatch(
							setToast({
								error: false,
								label: translate('user.details.numbers.detached'),
								actionType: translate('detached'),
							})
						);
						invalidateCache(['getNumbersForUser'], true);
						fetchNumbers();
						setSelectedIndexes([]);
					}
				});
		}
	};

	return (
		<div className={classes.numbersWrapper}>
			<ConditionalRender
				show={!!user}
				onTrue={() => (
					<BreadcrumbsItem to={`/dashboard/users/${userId}/numbers`}>
						{user.userData.userName} - {translate('path.numbers')}
					</BreadcrumbsItem>
				)}
			/>
			<ConditionalRender
				show={!loading}
				onTrue={() => (
					<>
						{numbers.length > 0 ? (
							<div>
								<TableHeader
									searchInputValue={searchValue}
									setSearchInputValue={(value) => setSearchValue(value)}
									exportToXLS={exportToXls}
									buttons={{
										text: (
											<>
												<Add />
												{translate('add.number')}
											</>
										),
										onClick: () => setIsAddNumberDialogOpen(true),
										buttonProps: {
											withIcon: true,
										},
									}}
								/>
								<Table
									withCheckbox
									withHeader
									total={filteredNumbers.length}
									onSelect={(value) => setSelectedIndexes(value)}
									selected={selectedIndexes}
									selectPanel={{
										selectedElements: selectedIndexes.length,
										downloadMenuItems: [
											{
												name: 'XSLX',
												onClick: exportToXls,
											},
										],
										deleteItems: deleteNumbers,
										deleteItemsAction: 'user-numbers:detach-numbers',
									}}
									tableProps={{
										data: filteredNumbers,
										columns: [
											{
												Header: translate('table.phone.number'),
												Cell: ({ original }: { original: SingleNumber }) => (
													<LinkCell
														path={numberDetails(original)}
														text={original.accountNum}
														icon={<Phone />}
													/>
												),
												accessor: 'accountNum',
												minWidth: 200,
											},
											{
												Header: translate('table.service.type'),
												minWidth: 200,
												Cell: ({ original }: { original: SingleNumber }) => (
													<div className={classes.cell}>
														<TextCell
															text={`${capitalize(original.accountType)} - ${capitalize(
																original.billingType
															)}`}
														/>
													</div>
												),
												accessor: 'username',
											},
										],
										defaultPageSize: 10,
									}}
								/>
							</div>
						) : (
							<div className={classes.noNumbers}>
								<div>
									<BodyM disableMargin>{translate('user.doesnt.have.numbers')}</BodyM>
								</div>
								<CanPerform
									action="user-numbers:add-number"
									yes={
										<Button
											color="primary-light"
											withIcon
											onClick={() => setIsAddNumberDialogOpen(true)}
										>
											<>
												<Add />
												{translate('add.first.number')}
											</>
										</Button>
									}
								/>
							</div>
						)}
						<CanPerform
							action="users:add-numbers"
							yes={
								<AddNumberDialog
									isDialogOpen={isAddNumberDialogOpen}
									setIsDialogOpen={(value) => setIsAddNumberDialogOpen(value)}
									userId={user.userData.userId}
									numbers={availableNumbers}
									loading={loadingNumbers}
									numbersSuccessfullyAdded={(addedNumbers) => numbersSuccessfullyAdded(addedNumbers)}
									departments={departments}
									userDepartment={user.userData}
								/>
							}
						/>
					</>
				)}
			/>
		</div>
	);
};

export default Numbers;
